import { useState } from "react";
import { toast } from "sonner";
import { HardDrive } from "lucide-react";

const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const APP_ID = (CLIENT_ID || "").split("-")[0];
const SCOPE = "https://www.googleapis.com/auth/drive.readonly";

function loadScript(src) {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) return resolve();
    const s = document.createElement("script");
    s.src = src;
    s.onload = resolve;
    s.onerror = reject;
    document.body.appendChild(s);
  });
}

export function GoogleDrivePicker({ multiple = false, onPicked }) {
  const [busy, setBusy] = useState(false);

  const showPicker = (token) => {
    const g = window.google;
    const view = new g.picker.DocsView(g.picker.ViewId.DOCS)
      .setMimeTypes("application/pdf")
      .setIncludeFolders(true)
      .setSelectFolderEnabled(false);
    const builder = new g.picker.PickerBuilder()
      .setOAuthToken(token)
      .setAppId(APP_ID)
      .addView(view)
      .setCallback((data) => onPick(data, token));
    if (multiple) builder.enableFeature(g.picker.Feature.MULTISELECT_ENABLED);
    builder.build().setVisible(true);
  };

  const onPick = async (data, token) => {
    const g = window.google;
    if (data.action === g.picker.Action.CANCEL) { setBusy(false); return; }
    if (data.action !== g.picker.Action.PICKED) return;
    try {
      const docs = data.docs || [];
      const files = [];
      for (const doc of docs) {
        const r = await fetch(`https://www.googleapis.com/drive/v3/files/${doc.id}?alt=media`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) throw new Error("download failed");
        const blob = await r.blob();
        files.push(new File([blob], doc.name || `${doc.id}.pdf`, { type: "application/pdf" }));
      }
      if (files.length) { onPicked(files); toast.success(`${files.length} file dari Google Drive dipilih`); }
    } catch {
      toast.error("Gagal mengunduh file dari Google Drive");
    } finally {
      setBusy(false);
    }
  };

  const start = async () => {
    if (!CLIENT_ID) { toast.error("Google Client ID belum dikonfigurasi"); return; }
    setBusy(true);
    try {
      await Promise.all([
        loadScript("https://accounts.google.com/gsi/client"),
        loadScript("https://apis.google.com/js/api.js"),
      ]);
      await new Promise((res) => window.gapi.load("picker", res));
      const tokenClient = window.google.accounts.oauth2.initTokenClient({
        client_id: CLIENT_ID,
        scope: SCOPE,
        callback: (resp) => {
          if (resp.error) { toast.error("Gagal otorisasi Google Drive"); setBusy(false); return; }
          showPicker(resp.access_token);
        },
      });
      tokenClient.requestAccessToken({ prompt: "" });
    } catch {
      toast.error("Gagal memuat Google Picker");
      setBusy(false);
    }
  };

  return (
    <button type="button" onClick={start} disabled={busy} data-testid="gdrive-picker-btn"
      className="px-4 py-2 rounded-full border-2 border-[#4285F4] text-[#4285F4] text-sm font-medium inline-flex items-center gap-2 hover:bg-[#4285F4]/5 transition-colors disabled:opacity-60">
      <HardDrive className="h-4 w-4" /> {busy ? "Memuat…" : "Upload dari Google Drive"}
    </button>
  );
}
